import { Box, Dialog, DialogContent, List, ListItemButton, ListItemText, CircularProgress, Typography } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import PaperComponent from "components/PaperComponent"
import { DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"

const ManualDialog = ({ pageNum, dialogOpen, setDialogOpen }) => {
  const { ax } = useContext(GlobalStateContext)
  const [files, setFiles] = useState([])
  const [selectedFile, setSelectedFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [imageLoading, setImageLoading] = useState(false)


  const fetchFileList = useCallback(async () => {
    try {
      setLoading(true)
      const response = await ax.post("/core/getManualFileList", { pageNum })

      if (response.data.files?.length > 0) {
        setFiles(response.data.files)
        setImageLoading(true)
        setSelectedFile(response.data.files[0]) // Auto-select first file
      } else {
        setFiles([])
        setSelectedFile(null)
      }
    } catch (error) {
      console.error("Failed to fetch file list:", error)
      setFiles([])
    } finally {
      setLoading(false)
    }
  }, [ax, pageNum])

  const handleFileClick = useCallback((filename) => {
    setSelectedFile(o=> {
      if (o !== filename) {
        setImageLoading(true) // If same file clicked, stop loading
      }
      return filename
    })
  }, [])

  // Construct image URL from selected file
  const imageUrl = useMemo(() => {
    return selectedFile
      ? `${window.location.origin}/public/emily-manual-per-page/${pageNum}/${selectedFile}`
      : null
  }, [selectedFile, pageNum])


  // Fetch file list when dialog opens
  useEffect(() => {
    if (dialogOpen && pageNum) {
      fetchFileList()
    } else {
      setSelectedFile(null)
      setFiles([])
      setLoading(false)
      setImageLoading(false)
    }
  }, [dialogOpen, pageNum, fetchFileList])

  return (
    <Dialog
      open={dialogOpen}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth
      maxWidth='xl'
    >
      <DialogHeader
        handleDialogClose={() => setDialogOpen(false)}
        title={`คู่มือการใช้งาน - หน้า ${pageNum}`}
        onDeleteClick={null}
      />
      <DialogContent>
        <BoxFR sx={{ gap: 2, height: 800, pt: 2, alignItems: 'stretch' }}>
          {/* Left Panel - File List */}
          <BoxFC sx={{ flex: 1, minWidth: 200, maxWidth: 300, borderRight: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>รายการไฟล์</Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : files.length === 0 ? (
              <Typography color="text.secondary">ไม่พบไฟล์คู่มือ</Typography>
            ) : (
              <List sx={{ overflow: 'auto' }}>
                {files.map((file) => (
                  <ListItemButton
                    key={file}
                    selected={selectedFile === file}
                    onClick={() => handleFileClick(file)}
                  >
                    <ListItemText primary={file.replace(/\.[^.]+$/, '')} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </BoxFC>

          {/* Right Panel - Image Display */}
          <BoxFC sx={{ flex: 3, minWidth: 0, overflow: 'auto', alignItems: 'center', justifyContent: 'center' }}>
            {imageLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" position="absolute">
                <CircularProgress />
              </Box>
            )}
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={selectedFile}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  cursor: 'pointer',
                  display: imageLoading ? 'none' : 'block'
                }}
                onLoad={() => setImageLoading(false)}
                onClick={() => window.open(imageUrl, '_blank')} // Open in new tab on click
              />
            ) : (
              <Typography color="text.secondary">
                {files.length > 0 ? 'เลือกไฟล์เพื่อแสดง' : ''}
              </Typography>
            )}
          </BoxFC>
        </BoxFR>
      </DialogContent>
    </Dialog>
  )
}

export default React.memo(ManualDialog)